import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { OrdersToEmployeeApiService } from "src/app/core/api/orders-to-employee-api.service";
import { WorkorderApiService } from "src/app/core/api/workorder-api.service";
import { SchedulerApiService } from "src/app/core/api/scheduler-api.service";
import { IEmployeeDto } from "src/app/core/models/employee.model";
import { IOrdersToEmployeeDto, IOrdersToEmployeeResponse } from "src/app/core/models/orders-to-employee.model";
import { SchedulerField } from "src/app/core/models/scheduler.model";
import { IWorkorderDto, WorkorderField } from "src/app/core/models/workorder.model";
import * as moment from "moment";
import { WORKFLOW_WORKORDER_FIELDS, WORKFLOW_ORDERS_TO_EMPLOYEE_FIELDS } from "../shared/workflow-fields.constants";
import { WorkflowStatusService } from "./workflow-status.service";
import { SystemStatusSortOrder } from "../models/workflow-status.model";

/**
 * Cached workorders interface for type safety
 */
interface CachedWorkorders {
	data: IWorkorderDto[];
	timestamp: number;
}

/**
 * Centralized service for managing workflow data
 * Handles fetching, filtering, and caching of workorders
 */
@Injectable({
	providedIn: "root"
})
export class WorkflowDataService {
	// State management
	private activeStatusIdSubject$ = new BehaviorSubject<string | null>(null);
	private workordersSubject$ = new BehaviorSubject<IWorkorderDto[]>([]);
	private ordersToEmployeesSubject$ = new BehaviorSubject<IOrdersToEmployeeDto[]>([]);
	private loadingSubject$ = new BehaviorSubject<boolean>(false);
	private showAllOrdersSubject$ = new BehaviorSubject<boolean>(true);
	private activeEmployeeSubject$ = new BehaviorSubject<IEmployeeDto | null>(null);

	// Cache management with type safety
	private workordersCache = new Map<string, CachedWorkorders>();
	private ordersToEmployeesCache = new Map<string, IOrdersToEmployeeDto[]>();
	private readonly CACHE_TTL_MS = 30000; // 30 seconds — aligned with workflow polling interval
	private readonly API_BATCH_SIZE = 100; // API batch limit for order IDs

	// Public observables
	activeStatusId$ = this.activeStatusIdSubject$.asObservable();
	workorders$ = this.workordersSubject$.asObservable();
	ordersToEmployees$ = this.ordersToEmployeesSubject$.asObservable();
	loading$ = this.loadingSubject$.asObservable();
	showAllOrders$ = this.showAllOrdersSubject$.asObservable();
	activeEmployee$ = this.activeEmployeeSubject$.asObservable();

	// Fields to fetch from API - using shared constants for consistency
	private workorderFields = WORKFLOW_WORKORDER_FIELDS;
	private ordersToEmployeeFields = WORKFLOW_ORDERS_TO_EMPLOYEE_FIELDS;

	// The "show all orders" choice lives in localStorage instead of the (Dexie) StorageService,
	// because StorageService.clearStorage() wipes everything on logout, a failed login and every
	// token refresh failure — which reset the toggle over and over. Stored as a map keyed by
	// employee_id, since workshop tablets are shared between employees.
	private static readonly SHOW_ALL_ORDERS_STORAGE_KEY = "af.workflow.showAllOrders";

	constructor(
		private workorderApiService: WorkorderApiService,
		private ordersToEmployeeApiService: OrdersToEmployeeApiService,
		private workflowStatusService: WorkflowStatusService,
		private schedulerApiService: SchedulerApiService
	) {}

	/**
	 * Set the active employee and restore their remembered "show all orders" choice.
	 *
	 * Employees who may not see all orders are pinned to "only my orders". That is not persisted,
	 * so their own choice comes back if they are later given the "Toon alle orders" setting.
	 */
	setActiveEmployee(employee: IEmployeeDto): void {
		this.activeEmployeeSubject$.next(employee);
		this.showAllOrdersSubject$.next(this.canSeeAllOrders(employee) ? this.readStoredShowAllOrders(employee.employee_id) : false);
	}

	/**
	 * Remembered "show all orders" choice for this employee, defaulting to true (show all)
	 * when the employee has not used the toggle yet.
	 */
	private readStoredShowAllOrders(employeeId: string | undefined): boolean {
		const stored = employeeId ? this.readShowAllOrdersPreferences()[employeeId] : undefined;
		return typeof stored === "boolean" ? stored : true;
	}

	private readShowAllOrdersPreferences(): Record<string, boolean> {
		try {
			const stored = localStorage.getItem(WorkflowDataService.SHOW_ALL_ORDERS_STORAGE_KEY);
			const parsed = stored ? JSON.parse(stored) : null;
			return parsed && typeof parsed === "object" ? parsed : {};
		} catch (error) {
			console.error("Error reading show all orders preference:", error);
			return {};
		}
	}

	private persistShowAllOrders(employeeId: string | undefined, showAll: boolean): void {
		if (!employeeId) {
			return;
		}

		try {
			const preferences = this.readShowAllOrdersPreferences();
			preferences[employeeId] = showAll;
			localStorage.setItem(WorkflowDataService.SHOW_ALL_ORDERS_STORAGE_KEY, JSON.stringify(preferences));
		} catch (error) {
			console.error("Error saving show all orders preference:", error);
		}
	}

	/**
	 * Whether this employee is allowed to see orders they are not coupled to.
	 *
	 * Driven by the "Toon alle orders" employee setting (is_show_all_orders_in_app) — the same
	 * setting the AF10 workflow itself uses. Roles that manage the whole workshop (administratie,
	 * chef werkplaats, eigenaar) keep full visibility, because in AF10 the "Toon alle orders"
	 * checkbox is only shown for employees marked as monteur.
	 *
	 * TEN-22502: is_show_more_employees_in_work ("Workflow, monteur kiezen") must NOT be used here.
	 * That setting only controls whether the user may change the mechanic on an order and defaults
	 * to 1 for every new employee, so it made every mechanic see all orders.
	 */
	canSeeAllOrders(employee: IEmployeeDto | null): boolean {
		if (!employee) {
			return false;
		}

		return employee.is_show_all_orders_in_app === 1 || employee.is_administrator === 1 || employee.is_chief_workshop === 1 || employee.is_owner === 1;
	}

	/**
	 * Get current active status ID
	 */
	getActiveStatusId(): string | null {
		return this.activeStatusIdSubject$.value;
	}
	/**
	 * Load workorders for a specific status
	 * @param statusId - The workflow_status_id to load workorders for (NEW field)
	 * @param forceRefresh - Force reload even if cached
	 */
	async loadWorkordersForStatus(statusId: string, forceRefresh = false): Promise<void> {
		this.activeStatusIdSubject$.next(statusId);
		this.loadingSubject$.next(true);

		try {
			const employee = this.activeEmployeeSubject$.value;
			if (!employee) {
				console.warn("No active employee set");
				this.workordersSubject$.next([]);
				this.loadingSubject$.next(false);
				return;
			}

			// Check cache
			// Include showAllOrders in key so toggling the filter never serves stale data
			const cacheKey = `${statusId}-${employee.employee_id}-${this.showAllOrdersSubject$.value}`;
			const cached = this.workordersCache.get(cacheKey);
			const now = Date.now();

			if (!forceRefresh && cached && now - cached.timestamp < this.CACHE_TTL_MS) {
				this.workordersSubject$.next(cached.data);
				this.loadingSubject$.next(false);
				return;
			}

			// Fetch fresh data using NEW workflow_status_id field
			const workorders = await this.fetchWorkordersForStatus(statusId);

			// Fetch orders to employees for these workorders
			const ordersToEmployees = await this.fetchOrdersToEmployees(workorders);

			// Treat scheduler (extra planning) event mechanics as coupled employees: append synthetic
			// orders_to_employees entries so avatars, clocking visibility and the clocking modal behave
			// the same as a normal orders_to_employees link. Scoped to the fetched orders (order_id[in]).
			const schedulerLinks = await this.fetchSchedulerLinks(workorders);
			this.applySchedulerLinks(ordersToEmployees, schedulerLinks);

			// Clear per-order cache so getOrdersToEmployeesByOrderId() returns fresh data
			this.ordersToEmployeesCache.clear();
			this.ordersToEmployeesSubject$.next(ordersToEmployees);

			// Order IDs the active employee is linked to via their own scheduler events (for visibility).
			const mySchedulerOrderIds = new Set(schedulerLinks.filter((link) => link.employee_id === employee.employee_id).map((link) => link.order_id));

			// Filter based on permissions
			const filteredWorkorders = this.filterWorkordersByPermission(
				workorders,
				employee,
				this.showAllOrdersSubject$.value,
				ordersToEmployees,
				mySchedulerOrderIds
			);

			// Process custom descriptions
			filteredWorkorders.forEach((workorder) => {
				if (workorder.is_custom_description === undefined || workorder.is_custom_description === null) {
					workorder.is_custom_description = workorder.order_description_id ? 0 : 1;
				}
			});

			// Sort: clocked-on orders first (own first, others second), then by planning_start_date
			const sortedWorkorders = this.sortWorkorders(filteredWorkorders, employee);

			// Update cache
			this.workordersCache.set(cacheKey, {
				data: sortedWorkorders,
				timestamp: now
			});

			// Update state
			this.workordersSubject$.next(sortedWorkorders);
		} catch (error) {
			console.error("Error loading workorders:", error);
			this.workordersSubject$.next([]);
		} finally {
			this.loadingSubject$.next(false);
		}
	}

	/**
	 * Fetch workorders for a specific status from API
	 * Uses workflow_status_id field to filter workorders
	 */
	private async fetchWorkordersForStatus(workflowStatusId: string): Promise<IWorkorderDto[]> {
		// Check if this is the "Gereed" (finished) status by checking sort_order
		// Dynamically determine finished status from WorkflowStatusService
		const status = this.workflowStatusService.getStatusById(workflowStatusId);
		const isFinishedStatus = status?.sortOrder === SystemStatusSortOrder.WORKSHOP_READY;

		if (isFinishedStatus) {
			// Fetch regular, invoiced, and CI orders in parallel
			const [regularOrders, invoicedOrders, ciOrders] = await Promise.all([
				this.fetchRegularWorkorders(workflowStatusId),
				this.fetchInvoicedWorkorders(workflowStatusId),
				this.fetchCiWorkorders(workflowStatusId)
			]);

			return [...regularOrders, ...invoicedOrders, ...ciOrders];
		} else {
			return this.fetchRegularWorkorders(workflowStatusId);
		}
	}

	/**
	 * Fetch regular (non-invoiced, non-CI) workorders
	 * Uses workflow_status_id to filter workorders
	 */
	private async fetchRegularWorkorders(workflowStatusId: string): Promise<IWorkorderDto[]> {
		const filter = `is_in_workflow[eq]:1,is_invoiced[eq]:0,order_type[eq]:1,workflow_status_id[nq]:null,workflow_status_id[eq]:${workflowStatusId},(is_collect[eq]:null,-is_collect[eq]:0),(is_ci_printed[eq]:null,-is_ci_printed[eq]:0)`;
		return this.fetchAllPages(filter);
	}

	/**
	 * Fetch invoiced workorders (last 2 days)
	 * Uses workflow_status_id to filter workorders
	 */
	private async fetchInvoicedWorkorders(workflowStatusId: string): Promise<IWorkorderDto[]> {
		const invoiceDate = moment().utc(true).subtract(2, "days").startOf("day").format("YYYY-MM-DD");
		const filter = `(-invoice_date[ge]:"${invoiceDate}",is_in_workflow[eq]:1,is_invoiced[eq]:1,order_type[eq]:1,workflow_status_id[eq]:${workflowStatusId})`;

		try {
			const response = await firstValueFrom(this.workorderApiService.getWorkorders(1, this.workorderFields, filter, WorkorderField.InvoiceDate));
			return response.data || [];
		} catch (error) {
			console.error("Error fetching invoiced workorders:", error);
			return [];
		}
	}

	/**
	 * Fetch CI printed workorders (last 2 days)
	 * Uses workflow_status_id to filter workorders
	 */
	private async fetchCiWorkorders(workflowStatusId: string): Promise<IWorkorderDto[]> {
		const invoiceDate = moment().utc(true).subtract(2, "days").startOf("day").format("YYYY-MM-DD");
		const filter = `(-invoice_date[ge]:"${invoiceDate}",is_invoiced[eq]:0,is_ci_printed[eq]:1,order_type[eq]:1,workflow_status_id[nq]:null,workflow_status_id[eq]:${workflowStatusId})`;

		try {
			const response = await firstValueFrom(this.workorderApiService.getWorkorders(1, this.workorderFields, filter, WorkorderField.InvoiceDate));
			return response.data || [];
		} catch (error) {
			console.error("Error fetching CI workorders:", error);
			return [];
		}
	}

	/**
	 * Fetch scheduler (extra planning) events for the given orders and return them as
	 * {order_id, employee_id} links (all employees). Scoped with order_id[in] and batched,
	 * mirroring fetchOrdersToEmployees — so we never fetch the employee's entire event history.
	 */
	private async fetchSchedulerLinks(workorders: IWorkorderDto[]): Promise<IOrdersToEmployeeDto[]> {
		const links: IOrdersToEmployeeDto[] = [];
		const orderIds = (workorders || []).map((order) => order.order_id).filter(Boolean);
		if (orderIds.length === 0) {
			return links;
		}

		try {
			const promises = [];
			for (let i = 0; i < orderIds.length; i += this.API_BATCH_SIZE) {
				const chunk = orderIds.slice(i, i + this.API_BATCH_SIZE);
				const filter = `order_id[in]:"${chunk.join(";")}"`;
				promises.push(
					firstValueFrom(this.schedulerApiService.getSchedulers(1, [SchedulerField.SchedulerId, SchedulerField.OrderId, SchedulerField.EmployeeId], filter))
				);
			}

			const results = await Promise.all(promises);
			for (const response of results) {
				for (const event of response.data || []) {
					if (event.order_id && event.employee_id) links.push({ order_id: event.order_id, employee_id: event.employee_id });
				}
			}
		} catch (error) {
			console.error("Error fetching scheduler links:", error);
		}

		return links;
	}

	/**
	 * Append synthetic orders_to_employees entries for scheduler event mechanics into the flat list
	 * (de-duplicated by order + employee). This list feeds getOrdersToEmployeesByOrderId(), which drives
	 * the workflow card avatars and clocking visibility — so event mechanics are treated as coupled.
	 */
	private applySchedulerLinks(ordersToEmployees: IOrdersToEmployeeDto[], links: IOrdersToEmployeeDto[]): void {
		if (!links || links.length === 0) {
			return;
		}

		const pairKey = (orderId: string, employeeId: string) => `${orderId}|${employeeId}`;
		const existingPairs = new Set(ordersToEmployees.map((ote) => pairKey(ote.order_id, ote.employee_id)));

		for (const link of links) {
			const key = pairKey(link.order_id, link.employee_id);
			if (!existingPairs.has(key)) {
				existingPairs.add(key);
				ordersToEmployees.push({ order_id: link.order_id, employee_id: link.employee_id });
			}
		}
	}

	/**
	 * Fetch all pages of workorders recursively
	 */
	private async fetchAllPages(filter: string, page = 1, accumulated: IWorkorderDto[] = []): Promise<IWorkorderDto[]> {
		try {
			const response = await firstValueFrom(this.workorderApiService.getWorkorders(page, this.workorderFields, filter, WorkorderField.PlanningStartDate));

			const allData = [...accumulated, ...response.data];

			if (response.nextpage) {
				return this.fetchAllPages(filter, page + 1, allData);
			}

			return allData;
		} catch (error) {
			console.error("Error fetching workorders page:", error);
			return accumulated;
		}
	}

	/**
	 * Fetch orders to employees for given workorders
	 */
	private async fetchOrdersToEmployees(workorders: IWorkorderDto[]): Promise<IOrdersToEmployeeDto[]> {
		if (!workorders || workorders.length === 0) {
			return [];
		}

		const orderIds = workorders.map((order) => order.order_id).filter(Boolean);
		if (orderIds.length === 0) {
			return [];
		}

		try {
			// Batch API calls in chunks to avoid URL length limits
			const promises: Promise<IOrdersToEmployeeResponse>[] = [];

			for (let i = 0; i < orderIds.length; i += this.API_BATCH_SIZE) {
				const chunk = orderIds.slice(i, i + this.API_BATCH_SIZE);
				const filter = `order_id[in]:"${chunk.join(";")}"`;
				promises.push(firstValueFrom(this.ordersToEmployeeApiService.getOrdersToEmployeByOrderId(this.ordersToEmployeeFields, filter)));
			}

			const results = await Promise.all(promises);
			return results.reduce((acc, val) => acc.concat(val.data), []) as IOrdersToEmployeeDto[];
		} catch (error) {
			console.error("Error fetching orders to employees:", error);
			return [];
		}
	}

	/**
	 * Sort workorders so that clocked-on orders appear first within each lane.
	 * Priority order:
	 *   1. Orders the current employee is actively clocked on (sorted by planning_start_date)
	 *   2. Orders any other employee is actively clocked on (sorted by planning_start_date)
	 *   3. All remaining orders (sorted by planning_start_date)
	 */
	private sortWorkorders(workorders: IWorkorderDto[], currentEmployee: IEmployeeDto): IWorkorderDto[] {
		const byPlanningDate = (a: IWorkorderDto, b: IWorkorderDto): number => {
			const dateA = a.planning_start_date ?? "";
			const dateB = b.planning_start_date ?? "";
			return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
		};

		const myClocked: IWorkorderDto[] = [];
		const othersClocked: IWorkorderDto[] = [];
		const rest: IWorkorderDto[] = [];

		for (const workorder of workorders) {
			const timesheets: any[] = workorder.timesheets ?? [];
			const isMineClocked = timesheets.some((t) => t.employee_id === currentEmployee.employee_id && !t.end_time);
			const isOtherClocked = !isMineClocked && timesheets.some((t) => !t.end_time);

			if (isMineClocked) {
				myClocked.push(workorder);
			} else if (isOtherClocked) {
				othersClocked.push(workorder);
			} else {
				rest.push(workorder);
			}
		}

		return [...myClocked.sort(byPlanningDate), ...othersClocked.sort(byPlanningDate), ...rest.sort(byPlanningDate)];
	}

	/**
	 * Filter workorders based on employee permissions and settings
	 */
	private filterWorkordersByPermission(
		workorders: IWorkorderDto[],
		employee: IEmployeeDto,
		showAllOrders: boolean,
		ordersToEmployees: IOrdersToEmployeeDto[],
		schedulerOrderIds: Set<string> = new Set()
	): IWorkorderDto[] {
		// Only employees with "Toon alle orders" (plus admins, chief workshops and owners) are
		// allowed to see all orders. Enforce this at the data layer regardless of what is
		// stored in the toggle, so a stored "true" never leaks to an unpermitted mechanic.
		const hasPermission = this.canSeeAllOrders(employee);

		// If showing all orders AND the employee has permission, return all
		if (showAllOrders && hasPermission) {
			return workorders;
		}

		// Filter to only show orders assigned to this employee, either via the orders_to_employees
		// relation or via a scheduler (extra planning) event linking the employee to the order.
		const employeeId = employee.employee_id;
		return workorders.filter((workorder) => {
			return workorder.orders_to_employees?.some((ote) => ote.employee_id === employeeId) || schedulerOrderIds.has(workorder.order_id);
		});
	}

	/**
	 * Get orders to employees for a specific order ID
	 */
	getOrdersToEmployeesByOrderId(orderId: string): IOrdersToEmployeeDto[] {
		// Check cache first
		if (this.ordersToEmployeesCache.has(orderId)) {
			return this.ordersToEmployeesCache.get(orderId)!;
		}

		// Filter from current state
		const ordersToEmployees = this.ordersToEmployeesSubject$.value;
		const filtered = ordersToEmployees.filter((ote) => ote.order_id === orderId);

		// Cache result
		this.ordersToEmployeesCache.set(orderId, filtered);

		return filtered;
	}

	/**
	 * Toggle show all orders setting
	 * Optimized: Only writes to storage if value actually changed
	 */
	async toggleShowAllOrders(showAll: boolean): Promise<void> {
		const currentValue = this.showAllOrdersSubject$.value;

		// Only update if value changed (avoid unnecessary storage writes)
		if (currentValue === showAll) {
			return;
		}

		this.showAllOrdersSubject$.next(showAll);

		// Remember the choice for this employee so it survives navigation, app restarts and logout
		this.persistShowAllOrders(this.activeEmployeeSubject$.value?.employee_id, showAll);

		// Reload current status with new filter
		const currentStatusId = this.activeStatusIdSubject$.value;
		if (currentStatusId) {
			await this.loadWorkordersForStatus(currentStatusId, true);
		}
	}

	/**
	 * Refresh the current status (force reload)
	 */
	async refreshCurrentStatus(): Promise<void> {
		const currentStatusId = this.activeStatusIdSubject$.value;
		if (currentStatusId) {
			await this.loadWorkordersForStatus(currentStatusId, true);
		}
	}

	/**
	 * Silently refresh the current status without showing a loading spinner.
	 * Use after mechanic add/remove to avoid full-list flicker.
	 */
	async refreshCurrentStatusSilent(): Promise<void> {
		const currentStatusId = this.activeStatusIdSubject$.value;
		if (currentStatusId) {
			await this.loadWorkordersForStatusSilent(currentStatusId);
		}
	}

	/**
	 * Load workorders for a status without triggering loading state
	 *
	 * SILENT LOADING - No loading spinner, prevents UI flicker
	 * Used when:
	 * - Toggling "show all orders" filter
	 * - Priority loading of active status during filter changes
	 * - Background updates that shouldn't show loading UI
	 *
	 * @param statusId - The workflow_status_id to load workorders for
	 * @returns Array of filtered workorders
	 */
	async loadWorkordersForStatusSilent(statusId: string): Promise<IWorkorderDto[]> {
		try {
			const employee = this.activeEmployeeSubject$.value;
			if (!employee) {
				console.warn("No active employee set");
				return [];
			}

			// Clear cache for this status since filter changed
			const cacheKey = `${statusId}-${employee.employee_id}-${this.showAllOrdersSubject$.value}`;
			this.workordersCache.delete(cacheKey);

			// Fetch fresh data
			const workorders = await this.fetchWorkordersForStatus(statusId);

			// Fetch orders to employees for these workorders
			const ordersToEmployees = await this.fetchOrdersToEmployees(workorders);

			// Append synthetic orders_to_employees entries for scheduler (extra planning) event mechanics.
			const schedulerLinks = await this.fetchSchedulerLinks(workorders);
			this.applySchedulerLinks(ordersToEmployees, schedulerLinks);

			// Clear per-order cache so getOrdersToEmployeesByOrderId() returns fresh data
			this.ordersToEmployeesCache.clear();
			this.ordersToEmployeesSubject$.next(ordersToEmployees);

			// Order IDs the active employee is linked to via their own scheduler events (for visibility).
			const mySchedulerOrderIds = new Set(schedulerLinks.filter((link) => link.employee_id === employee.employee_id).map((link) => link.order_id));

			// Filter based on permissions
			const filteredWorkorders = this.filterWorkordersByPermission(
				workorders,
				employee,
				this.showAllOrdersSubject$.value,
				ordersToEmployees,
				mySchedulerOrderIds
			);

			// Process custom descriptions
			filteredWorkorders.forEach((workorder) => {
				if (workorder.is_custom_description === undefined || workorder.is_custom_description === null) {
					workorder.is_custom_description = workorder.order_description_id ? 0 : 1;
				}
			});

			// Sort: clocked-on orders first (own first, others second), then by planning_start_date
			const sortedWorkorders = this.sortWorkorders(filteredWorkorders, employee);

			// Update cache
			this.workordersCache.set(cacheKey, {
				data: sortedWorkorders,
				timestamp: Date.now()
			});

			// Update state (without loading indicator)
			this.workordersSubject$.next(sortedWorkorders);

			return sortedWorkorders;
		} catch (error) {
			console.error("Error loading workorders silently:", error);
			return [];
		}
	}

	/**
	 * Clear all caches
	 */
	clearCache(): void {
		this.workordersCache.clear();
		this.ordersToEmployeesCache.clear();
	}

	/**
	 * Invalidate cache for specific status
	 * Used when a workorder moves between statuses
	 */
	invalidateStatusCache(statusId: string): void {
		const employee = this.activeEmployeeSubject$.value;
		if (!employee) return;

		// Invalidate for both toggle states since a status move affects both views
		this.workordersCache.delete(`${statusId}-${employee.employee_id}-true`);
		this.workordersCache.delete(`${statusId}-${employee.employee_id}-false`);
	}

	/**
	 * Invalidate cached workorder lists for all statuses except the currently active one.
	 * Called after pull-to-refresh so that navigating to any inactive tab always
	 * fetches a fresh list from the API instead of serving stale cached data.
	 */
	invalidateInactiveStatusCaches(): void {
		const activeStatusId = this.activeStatusIdSubject$.value;
		const employee = this.activeEmployeeSubject$.value;
		if (!employee) return;

		for (const key of Array.from(this.workordersCache.keys())) {
			// Cache keys have the format: `${statusId}-${employeeId}-${showAll}`
			// Keep the active status cache; delete everything else.
			if (!key.startsWith(`${activeStatusId}-`)) {
				this.workordersCache.delete(key);
			}
		}
	}

	/**
	 * Get workorder count for each status (for badge display)
	 *
	 * BADGE COUNT LOGIC:
	 * - Fetches first page only (up to 100 items per status)
	 * - Returns actual count if < 100, otherwise returns 100
	 * - UI shows "100+" for unvisited statuses with count = 100
	 * - Respects "show all orders" toggle:
	 *   - ON: All workorders regardless of assignment
	 *   - OFF: Only workorders assigned to current employee (via orders_to_employees)
	 *
	 * EMPLOYEE FILTERING:
	 * When showAllOrders = false, filters by checking orders_to_employees relationship.
	 * Uses in-memory filtering (API doesn't support [cs] operator on relationships).
	 *
	 * @param statusIds - Array of workflow_status_ids to get counts for
	 * @returns Map of status IDs to counts (max 100 per status)
	 */
	async getWorkorderCountsForStatuses(statusIds: string[]): Promise<Map<string, number>> {
		const counts = new Map<string, number>();
		const employee = this.activeEmployeeSubject$.value;

		if (!employee || statusIds.length === 0) {
			return counts;
		}

		const showAllOrders = this.showAllOrdersSubject$.value;
		// Enforce permission at the data layer (mirrors filterWorkordersByPermission)
		const hasPermission = this.canSeeAllOrders(employee);
		const effectiveShowAll = showAllOrders && hasPermission;

		// Fetch counts in parallel for all statuses
		const countPromises = statusIds.map(async (statusId) => {
			try {
				// Check if this is the "Gereed" (WORKSHOP_READY) status
				// It uses a different multi-query approach for fetching workorders,
				// so badge count must mirror those same filters to stay consistent.
				const status = this.workflowStatusService.getStatusById(statusId);
				const isFinishedStatus = status?.sortOrder === SystemStatusSortOrder.WORKSHOP_READY;

				if (isFinishedStatus) {
					// Mirror fetchWorkordersForStatus: count across all three order categories
					const countFields = effectiveShowAll ? [WorkorderField.OrderId] : [WorkorderField.OrderId, WorkorderField.RmOrderToEmployeesEmployeeId];

					const invoiceDate = moment().utc(true).subtract(2, "days").startOf("day").format("YYYY-MM-DD");

					const regularFilter = `is_in_workflow[eq]:1,is_invoiced[eq]:0,order_type[eq]:1,workflow_status_id[nq]:null,workflow_status_id[eq]:${statusId},(is_collect[eq]:null,-is_collect[eq]:0),(is_ci_printed[eq]:null,-is_ci_printed[eq]:0)`;
					const invoicedFilter = `(-invoice_date[ge]:"${invoiceDate}",is_in_workflow[eq]:1,is_invoiced[eq]:1,order_type[eq]:1,workflow_status_id[eq]:${statusId})`;
					const ciFilter = `(-invoice_date[ge]:"${invoiceDate}",is_invoiced[eq]:0,is_ci_printed[eq]:1,order_type[eq]:1,workflow_status_id[nq]:null,workflow_status_id[eq]:${statusId})`;

					const [regularResponse, invoicedResponse, ciResponse] = await Promise.all([
						firstValueFrom(this.workorderApiService.getWorkorders(1, countFields, regularFilter)),
						firstValueFrom(this.workorderApiService.getWorkorders(1, countFields, invoicedFilter, WorkorderField.InvoiceDate)).catch(() => ({
							data: [] as IWorkorderDto[],
							nextpage: false
						})),
						firstValueFrom(this.workorderApiService.getWorkorders(1, countFields, ciFilter, WorkorderField.InvoiceDate)).catch(() => ({
							data: [] as IWorkorderDto[],
							nextpage: false
						}))
					]);

					let allWorkorders = [...(regularResponse.data || []), ...(invoicedResponse.data || []), ...(ciResponse.data || [])];

					if (!effectiveShowAll) {
						// Scoped to the fetched orders so badge count includes scheduler-linked orders, like the list.
						const schedulerLinks = await this.fetchSchedulerLinks(allWorkorders);
						const schedulerOrderIds = new Set(schedulerLinks.filter((link) => link.employee_id === employee.employee_id).map((link) => link.order_id));
						allWorkorders = allWorkorders.filter(
							(workorder) =>
								workorder.orders_to_employees?.some((ote: any) => ote.employee_id === employee.employee_id) || schedulerOrderIds.has(workorder.order_id)
						);
					}

					return { statusId, count: allWorkorders.length };
				}

				// Mirror fetchRegularWorkorders filter exactly so badge count matches list count.
				// The loose filter includes invoiced, collect, and CI-printed orders that
				// fetchRegularWorkorders excludes — causing an off-by-N discrepancy.
				const filter = `is_in_workflow[eq]:1,is_invoiced[eq]:0,order_type[eq]:1,workflow_status_id[nq]:null,workflow_status_id[eq]:${statusId},(is_collect[eq]:null,-is_collect[eq]:0),(is_ci_printed[eq]:null,-is_ci_printed[eq]:0)`;

				// Query with minimal fields including employee relationship
				// Page size 100 to detect counts up to 100
				const fields = effectiveShowAll
					? [WorkorderField.OrderId] // No need for relationship data if showing all
					: [WorkorderField.OrderId, WorkorderField.RmOrderToEmployeesEmployeeId]; // Need relationship for filtering

				const response = await firstValueFrom(this.workorderApiService.getWorkorders(1, fields, filter));

				let workorders = response.data || [];

				// Filter by employee if not showing all orders.
				// Use orders_to_employees (relation array) — the API always returns
				// relationship data under this key regardless of which rm_ field was requested.
				if (!effectiveShowAll) {
					// Scoped to the fetched orders so badge count includes scheduler-linked orders, like the list.
					const schedulerLinks = await this.fetchSchedulerLinks(workorders);
					const schedulerOrderIds = new Set(schedulerLinks.filter((link) => link.employee_id === employee.employee_id).map((link) => link.order_id));
					workorders = workorders.filter((workorder) => {
						return workorder.orders_to_employees?.some((ote: any) => ote.employee_id === employee.employee_id) || schedulerOrderIds.has(workorder.order_id);
					});
				}

				// Calculate count: if we got 100 and there's a next page, return 100+
				const count = workorders.length;
				const hasMore = response.nextpage === true;
				const finalCount = hasMore && count >= 100 ? 100 : count;

				return { statusId, count: finalCount };
			} catch (error) {
				console.error(`Error fetching count for status ${statusId}:`, error);
				return { statusId, count: 0 };
			}
		});

		const results = await Promise.all(countPromises);
		results.forEach(({ statusId, count }) => counts.set(statusId, count));

		return counts;
	}
}
